import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import SEO from '../components/SEO';
import '../components/Questionnaire.css';

const Questionnaire = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const totalQuestions = questions.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { [id]: value or [values] }

  // Fetch questions from API on component mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const url = `${API_BASE_URL}/api/questions`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to fetch questions');
        }
        
        const data = await response.json();
        setQuestions(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questions. Please refresh the page.');
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);
  
  // Get user email from localStorage
  const getUserEmail = () => {
    try {
      const leadInfo = JSON.parse(localStorage.getItem('leadInfo') || '{}');
      return leadInfo.email || localStorage.getItem('userEmail') || '';
    } catch {
      return localStorage.getItem('userEmail') || '';
    }
  };

  // Function to send answer to API immediately
  const sendAnswerToAPI = async (questionId, answerValue, questionType) => {
    const email = getUserEmail();
    if (!email) {
      console.warn('No email found, cannot save answer');
      return;
    }

    const questionKey = `Q${questionId}`;
    let formattedAnswer = answerValue;

    // Format answer based on type
    const question = questions.find(q => q.id === questionId);
    if (!question) {
      console.warn(`Question ${questionId} not found`);
      return;
    }

    if (questionType === 'single') {
      // Find the index and convert to letter (A, B, C, etc.)
      if (question.options && question.options.includes(answerValue)) {
        const optionIndex = question.options.indexOf(answerValue);
        formattedAnswer = String.fromCharCode(65 + optionIndex); // A, B, C, etc.
      }
    } else if (questionType === 'multi') {
      // Convert array of options to array of letters
      if (question.options && Array.isArray(answerValue)) {
        formattedAnswer = answerValue
          .filter(option => question.options.includes(option))
          .map(option => {
            const optionIndex = question.options.indexOf(option);
            return String.fromCharCode(65 + optionIndex);
          });
      }
    } else if (questionType === 'text') {
      // Text answers are sent as-is
      formattedAnswer = String(answerValue).trim() || "";
    }

    try {
      const url = `${API_BASE_URL}/api/Leads/answer`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          questionKey: questionKey,
          answerValue: formattedAnswer,
          answerType: questionType
        })
      });

      if (!response.ok) {
        console.error('Failed to save answer:', questionKey);
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      // Don't block user experience if API call fails
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const current = questions[currentIndex];

  const percentComplete = Math.round((currentIndex / totalQuestions) * 100);
  const progressFillWidth = percentComplete === 0 ? '2px' : `${percentComplete}%`;

  const handleSingleSelect = (option) => {
    const newAnswer = option;
    setAnswers(prev => ({ ...prev, [current.id]: newAnswer }));
    
    // Send answer to API immediately
    sendAnswerToAPI(current.id, newAnswer, 'single');
    
    // Auto-advance shortly after selection for single-choice
    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) setCurrentIndex(i => i + 1);
    }, 150);
  };

  const handleMultiToggle = (option) => {
    const existing = Array.isArray(answers[current.id]) ? answers[current.id] : [];
    const isSelected = existing.includes(option);
    let updated = existing;

    if (isSelected) {
      updated = existing.filter(o => o !== option);
    } else {
      const max = current.maxSelections || current.max_selections || Infinity;
      if (existing.length < max) {
        updated = [...existing, option];
      }
    }

    setAnswers(prev => ({ ...prev, [current.id]: updated }));
    
    // Send updated answer to API immediately
    sendAnswerToAPI(current.id, updated, 'multi');
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    setAnswers(prev => ({ ...prev, [current.id]: newValue }));
    
    // Debounce text input - send to API after user stops typing for 1 second
    // Clear any existing timeout
    if (handleTextChange.timeout) {
      clearTimeout(handleTextChange.timeout);
    }
    
    handleTextChange.timeout = setTimeout(() => {
      sendAnswerToAPI(current.id, newValue, 'text');
    }, 1000);
  };

  const canGoNext = () => {
    const value = answers[current?.id];
    if (!current) return false;
    if (current.type === 'text') {
      // Text questions are optional (can be empty)
      return true;
    }
    if (current.type === 'single') return !!value;
    if (current.type === 'multi') return Array.isArray(value) && value.length > 0;
    return false;
  };

  // Convert answers to backend format (Q1, Q2, etc. with letter answers)
  const formatAnswersForBackend = () => {
    const formattedAnswers = {};
    
    questions.forEach((question) => {
      // Use the database question ID to create the key (Q1, Q2, etc.)
      const questionKey = `Q${question.id}`;
      const answer = answers[question.id];
      
      // Handle empty/null answers
      if (answer === null || answer === undefined || answer === '') {
        formattedAnswers[questionKey] = null;
        return;
      }
      
      if (question.type === 'text') {
        // For text questions, send the text directly (trimmed)
        formattedAnswers[questionKey] = String(answer).trim() || "";
      } else if (question.type === 'single') {
        // For single choice, find the index of the selected option and convert to letter
        // Handle case where user changed answer - validate it still exists in options
        if (question.options.includes(answer)) {
          const optionIndex = question.options.indexOf(answer);
          formattedAnswers[questionKey] = String.fromCharCode(65 + optionIndex); // A, B, C, etc.
        } else {
          formattedAnswers[questionKey] = null;
        }
      } else if (question.type === 'multi') {
        // For multi choice, convert array of options to array of letters
        // Filter out any invalid options that might have been left from previous answers
        if (Array.isArray(answer) && answer.length > 0) {
          formattedAnswers[questionKey] = answer
            .filter(option => question.options.includes(option)) // Only include valid options
            .map(option => {
              const optionIndex = question.options.indexOf(option);
              return String.fromCharCode(65 + optionIndex); // A, B, C, etc.
            });
        } else {
          formattedAnswers[questionKey] = [];
        }
      }
    });
    
    return formattedAnswers;
  };

  // Scoring rules for each question (Q11 is text input, not included in scoring)
  const scoringRules = {
    Q1: { A: 3, B: 3, C: 2, D: 1 },
    Q2: { A: 3, B: 3, C: 3, D: 1, E: 1 },
    Q3: { A: 3, B: 3, C: 2, D: 1 },
    Q4: { A: 1, B: 2, C: 3, D: 1 },
    Q5: { A: 2, B: 2, C: 1, D: 2, E: 2, F: 1 },
    Q6: { A: 3, B: 2, C: 2, D: 3 },
    Q7: { A: 3, B: 3, C: 2, D: 1 },
    Q8: { A: 3, B: 2, C: 2, D: 3 },
    Q9: { A: 2, B: 2, C: 3, D: 3 },
    Q10: { A: 3, B: 3, C: 3, D: 2 }
  };

  // Category scoring rubric
  const categoryRubric = [
    { id: 1, category: "orderingEfficiency", weights: { A: 1, B: 2, C: 4, D: 3 } },
    { id: 2, category: "orderingEfficiency", weights: { A: 1, B: 1, C: 2, D: 2, E: 4 } },
    { id: 3, category: "orderingEfficiency", weights: { A: 1, B: 2, C: 3, D: 4 } },
    { id: 4, category: "orderingEfficiency", weights: { A: 4, B: 3, C: 1, D: 2 } },
    { id: 5, category: "customerEngagement", weights: { A: 3, B: 4, C: 4, D: 3, E: 3, F: 4 } },
    { id: 6, category: "orderingEfficiency", weights: { A: 1, B: 2, C: 4, D: 1 } },
    { id: 7, category: "customerEngagement", weights: { A: 1, B: 2, C: 3, D: 4 } },
    { id: 8, category: "orderingEfficiency", weights: { A: 1, B: 2, C: 4, D: 1 } },
    { id: 9, category: "orderingEfficiency", weights: { A: 1, B: 2, C: 3, D: 4 } },
    { id: 10, category: "customerEngagement", weights: { A: 1, B: 2, C: 3, D: 4 } },
    { id: 11, category: "customerEngagement", weights: {} }
  ];

  function calculateLeadScore(answers) {
    let total = 0;
  
    for (const [question, answer] of Object.entries(answers)) {
      // Skip Q11 (text question) as it doesn't have scoring rules
      if (question === "Q11") continue;
      
      // Skip null or invalid answers
      if (answer === null || answer === undefined) continue;
      
      if (question === "Q5") {
        // Handle multi-select: sum, then cap at 3
        if (Array.isArray(answer) && answer.length > 0) {
          const sum = answer.reduce((acc, opt) => {
            // Only count valid answer letters (A-F)
            if (typeof opt === 'string' && scoringRules.Q5[opt] !== undefined) {
              return acc + scoringRules.Q5[opt];
            }
            return acc;
          }, 0);
          total += Math.min(sum, 3);
        }
      } else if (scoringRules[question] && typeof answer === 'string') {
        // Only score valid answer letters
        if (scoringRules[question][answer] !== undefined) {
          total += scoringRules[question][answer];
        }
      }
    }
  
    if (total >= 25) return { score: total, status: "HOT" };   
    if (total >= 20) return { score: total, status: "WARM" };   
    return { score: total, status: "COLD" };
  }

  function calculateCategoryScores(rubric, responses) {
    const totals = {};
    const maxScores = {};
  
    for (const q of rubric) {
      const response = responses[q.id];
      // Skip if no response or if weights are empty (like Q11)
      if (!response || !q.weights || Object.keys(q.weights).length === 0) continue;
  
      const category = q.category;
      if (!totals[category]) {
        totals[category] = 0;
        maxScores[category] = 0;
      }
  
      const weightValues = Object.values(q.weights);
      if (weightValues.length === 0) continue;
      
      const maxWeight = Math.max(...weightValues);
  
      if (Array.isArray(response)) {
        // Multi-select (Q5): sum selected weights, max is based on max selections (3 for Q5)
        const selectedWeights = response
          .filter(opt => q.weights[opt] !== undefined)
          .map(opt => q.weights[opt] || 0);
        const sum = selectedWeights.reduce((a, b) => a + b, 0);
        // For multi-select, max possible is maxWeight * maxSelections (typically 3)
        const maxSelections = 3; // Q5 allows up to 3 selections
        const maxSum = maxWeight * maxSelections;
        totals[category] += sum;
        maxScores[category] += maxSum;
      } else if (typeof response === 'string') {
        // Single answer
        const weight = q.weights[response] || 0;
        totals[category] += weight;
        maxScores[category] += maxWeight;
      }
    }
  
    const results = {};
    for (const cat in totals) {
      const percent = maxScores[cat] > 0 ? (totals[cat] / maxScores[cat]) * 100 : 0;
      results[cat] = {
        score: totals[cat],
        max: maxScores[cat],
        percent: Math.round(percent)
      };
    }
  
    return results;
  }

  const goNext = async () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      // End of questionnaire - format and send to backend
      const formattedAnswers = formatAnswersForBackend();
      const leadScore = calculateLeadScore(formattedAnswers);
      
      // Convert Q1, Q2 format to database question ID format for category scoring
      // Note: The scoring rubric uses question IDs (1-11), so we need to map database IDs to rubric IDs
      // For now, we'll use the database question ID directly - you may need to adjust the scoring rubric
      const responsesForCategoryScoring = {};
      Object.keys(formattedAnswers).forEach(qKey => {
        const questionId = parseInt(qKey.substring(1)); // Extract number from "Q1", "Q2", etc.
        responsesForCategoryScoring[questionId] = formattedAnswers[qKey];
      });
      
      const categoryScores = calculateCategoryScores(categoryRubric, responsesForCategoryScoring);
      
      // Include lead score and category scores in the payload
      const payload = {
        ...formattedAnswers,
        leadScore: leadScore.score,
        leadStatus: leadScore.status,
        orderingEfficiencyScore: categoryScores.orderingEfficiency?.score || 0,
        orderingEfficiencyPercent: categoryScores.orderingEfficiency?.percent || 0,
        customerEngagementScore: categoryScores.customerEngagement?.score || 0,
        customerEngagementPercent: categoryScores.customerEngagement?.percent || 0
      };
      
      console.log('Formatted answers with scores:', payload);
      
      // Send final submission with scores to backend
      try {
        const url = `${API_BASE_URL}/api/Leads/submit`;
        const email = getUserEmail();
        const leadInfo = JSON.parse(localStorage.getItem('leadInfo') || '{}');
        
        await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            firstName: leadInfo.firstName,
            lastName: leadInfo.lastName,
            ...payload
          })
        });
      } catch (error) {
        console.error('Error submitting final questionnaire:', error);
        // Continue to results page even if API call fails
      }
      
      // Navigate to results page with scores
      navigate('/results', {
        state: {
          scores: {
            orderingEfficiencyPercent: categoryScores.orderingEfficiency?.percent || 0,
            customerEngagementPercent: categoryScores.customerEngagement?.percent || 0,
            orderingEfficiencyScore: categoryScores.orderingEfficiency?.score || 0,
            customerEngagementScore: categoryScores.customerEngagement?.score || 0,
            leadScore: leadScore.score,
            leadStatus: leadScore.status
          }
        }
      });
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="questionnaire-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>Loading questions...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="questionnaire-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
          <p style={{ color: 'red', marginBottom: '20px' }}>{error}</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      </div>
    );
  }

  // Show message if no questions
  if (questions.length === 0) {
    return (
      <div className="questionnaire-page">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>No questions available. Please contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Restaurant Online Ordering Assessment - Answer 10 Questions | Order Portal"
        description="Take our free restaurant online ordering assessment. Answer 10 quick questions about your restaurant's current online setup and get instant customized results. Perfect for pizza, deli, Chinese, Mexican, Thai, Indian, Japanese, and all restaurant types."
        keywords="restaurant assessment questions, online ordering quiz, restaurant digital assessment, restaurant evaluation, restaurant online presence test"
        canonical="https://orderportal.com/questionnaire"
      />
      <main className="questionnaire-page" role="main">
        {/* Header */}
        <header className="questionnaire-header">
        <div className="questionnaire-header-left">
          <button className="back-button" onClick={handleBackClick}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div className="questionnaire-logo">
            <img src="/myorderportal.png" alt="Order Portal - Restaurant Online Ordering Solutions" className="logo-image" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="questionnaire-content">
        <div className="question-container">
          <h1 className="question-text">
            {current?.question || current?.Question}
          </h1>

          {/* Options */}
          {current?.type !== 'text' && current?.options && (
            <div className="button-container">
              {current.options.map((option) => {
                const isActive = current.type === 'single'
                  ? answers[current.id] === option
                  : Array.isArray(answers[current.id]) && answers[current.id].includes(option);
                return (
                  <button
                    key={option}
                    className={`answer-button${isActive ? ' selected' : ''}`}
                    onClick={() =>
                      current.type === 'single' ? handleSingleSelect(option) : handleMultiToggle(option)
                    }
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          )}

          {current?.type === 'text' && (
            <div className="text-input-container">
              <textarea
                className="question-textarea"
                value={answers[current.id] || ''}
                onChange={handleTextChange}
                placeholder={current.placeholder || current.placeholderText || ''}
              />
            </div>
          )}

          {/* Navigation buttons */}
          {(current?.type === 'multi' || current?.type === 'text') && (
            <div className="nav-buttons" style={{ marginTop: '8px' }}>
              {currentIndex > 0 && (
                <button className="nav-button" onClick={goPrev}>
                  Back
                </button>
              )}
              <button className="nav-button" onClick={goNext} disabled={!canGoNext()}>
                {currentIndex === totalQuestions - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          )}
          {current?.type === 'single' && currentIndex > 0 && (
            <div className="nav-buttons" style={{ marginTop: '8px' }}>
              <button className="nav-button" onClick={goPrev}>
                Back
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer Progress Bar */}
      <footer className="questionnaire-footer">
        <div className="progress-container">
          <p className="progress-text">{percentComplete}% Complete</p>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: progressFillWidth }}></div>
          </div>
        </div>
      </footer>
      </main>
    </>
  );
};

export default Questionnaire;

